import React, {useState} from 'react';

import {Form} from 'react-bootstrap';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useRecoilState} from 'recoil';

import {authRedirectAtom} from 'modules/auth/state';
import {useAuth} from 'shared/hooks/useAuth';
import {getAxiosErrorOrThrow} from 'shared/utils/error';

import {Spinner} from 'shared/components/Spinner/Spinner';

type LoginFormData = {
    username: string;
    password: string;
};

export const HeroSectionLoginForm = () => {
    const {t} = useTranslation();

    const {register, handleSubmit} = useForm<LoginFormData>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const {authenticateWithCredentials} = useAuth();
    const navigate = useNavigate();

    const [authRedirect, setAuthRedirect] = useRecoilState(authRedirectAtom);
    const {search} = useLocation();

    React.useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const redirect = searchParams.get('redirect');
        setAuthRedirect(redirect || '/');
    }, [search, setAuthRedirect]);

    const redirectOnLogin = React.useCallback(() => {
        navigate(authRedirect || '/');
    }, [navigate, authRedirect]);

    const onSubmit = ({username, password}: LoginFormData) => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        (async () => {
            try {
                await authenticateWithCredentials({
                    username,
                    password,
                });
                redirectOnLogin();
            } catch (error) {
                toast.error(getAxiosErrorOrThrow(error));
            } finally {
                setIsSubmitting(false);
            }
        })();
    };
    
    return (
        <div>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group controlId="Login__username" className="FullScreenModal__form-group">
                    <Form.Control
                        className="FullScreenModal__form-control"
                        type="text"
                        placeholder={t('Login.username', 'Username or Email')}
                        disabled={isSubmitting}
                        autoFocus
                        autoComplete="username"
                        {...register('username')}
                    />
                </Form.Group>
                <Form.Group controlId="Login__password" className="FullScreenModal__form-group">
                    <Form.Control
                        type="password"
                        placeholder={t('common.password', 'Password')}
                        disabled={isSubmitting}
                        className="FullScreenModal__form-control Login__password-input"
                        autoComplete="current-password"
                        {...register('password')}
                    />
                </Form.Group>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="LoginForm--btn"
                >
                    {(isSubmitting) && (
                        <>
                            {t('Login.submitButton', 'Logging In', {context: 'busy'})}
                            <Spinner size="sm" />
                        </>
                    )}
                    {(!isSubmitting) && t('Login.submitButton', 'Log In')}
                </button>
            </Form>
        </div>
    );
};